import React, { useEffect, useState } from "react";
import { api, formatBRL } from "../lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRightLeft, X, Building2, Users } from "lucide-react";

/**
 * Modal de transferência de destino de capital de um contrato ATIVO.
 *
 * Suporta 3 fluxos:
 *   • Sócio → Sócio            (transferência clássica entre investidores)
 *   • Sócio → Capital Próprio   (contrato passa a ser 100% Forte Capital)
 *   • Capital Próprio → Sócio   (retorna o contrato para um sócio)
 *
 * Backend: POST /api/contracts/{id}/transfer-socio
 *   body:  { to_capital_source: 'empresa' } OU { new_socio_id: '<uuid>' }, reason (>=5)
 */
export default function TransferSocioModal({ contract, onClose, onDone }) {
  const [socios, setSocios] = useState([]);
  const [mode, setMode] = useState("socio"); // "socio" | "empresa"
  const [newSocioId, setNewSocioId] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const isEmpresaSource = contract.capital_source === "empresa";

  useEffect(() => {
    api.get("/socios")
      .then(r => setSocios(r.data.filter(s => s.status === "ativo" && s.id !== contract.socio_id)))
      .catch(() => setSocios([]));
  }, [contract.socio_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "socio" && !newSocioId) { toast.error("Selecione o novo sócio"); return; }
    if (reason.trim().length < 5) { toast.error("Informe o motivo (mínimo 5 caracteres)"); return; }
    setBusy(true);
    try {
      const body = { reason: reason.trim() };
      if (mode === "empresa") {
        body.to_capital_source = "empresa";
      } else {
        body.new_socio_id = newSocioId;
        body.to_capital_source = "socio";
      }
      await api.post(`/contracts/${contract.id}/transfer-socio`, body);
      toast.success(mode === "empresa"
        ? "Contrato transferido para Capital Próprio (100% Empresa)"
        : "Contrato transferido para o novo sócio");
      onDone && onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha ao transferir");
    } finally {
      setBusy(false);
    }
  };

  const currentLabel = isEmpresaSource
    ? "Capital Próprio (100% Empresa)"
    : (contract.socio_name || "—");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="transfer-socio-modal">
      <form onSubmit={submit} className="w-full max-w-lg rounded-lg border border-[#262626] bg-[#0d0d0d] shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
          <h3 className="font-display text-base flex items-center gap-2 text-[#E8C547]">
            <ArrowRightLeft size={16}/> Transferir destino do capital
          </h3>
          <button type="button" onClick={onClose} className="text-[#737373] hover:text-white" data-testid="transfer-socio-close">
            <X size={18}/>
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-[12px] text-amber-200/90">
            Esta ação <strong>realoca</strong> este contrato do destino atual para o novo destino.
            Pagamentos futuros e dashboards se ajustam automaticamente.
            Ledger e pagamentos passados permanecem vinculados ao destinatário anterior.
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="fc-label text-[10.5px]">Destino atual</label>
              <div className="fc-input bg-[#0a0a0a] text-[#A3A3A3] flex items-center justify-between" data-testid="transfer-current-socio">
                <span className="truncate">{currentLabel}</span>
              </div>
            </div>
            <div>
              <label className="fc-label text-[10.5px]">Capital do contrato</label>
              <div className="fc-input bg-[#0a0a0a] font-mono-tab text-[#E8C547]">
                {formatBRL(contract.capital)}
              </div>
            </div>
          </div>

          {/* Seletor: sócio vs Capital Próprio */}
          <div>
            <label className="fc-label">Novo destino</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <button
                type="button"
                onClick={() => setMode("socio")}
                className={`flex items-center gap-2 justify-center px-3 py-2 rounded border text-sm transition ${
                  mode === "socio"
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#E8C547]"
                    : "border-[#262626] text-[#A3A3A3] hover:border-[#404040]"
                }`}
                data-testid="transfer-mode-socio"
              >
                <Users size={14}/> Sócio investidor
              </button>
              <button
                type="button"
                onClick={() => setMode("empresa")}
                disabled={isEmpresaSource}
                className={`flex items-center gap-2 justify-center px-3 py-2 rounded border text-sm transition disabled:opacity-40 disabled:cursor-not-allowed ${
                  mode === "empresa"
                    ? "border-emerald-500 bg-emerald-500/10 text-emerald-300"
                    : "border-[#262626] text-[#A3A3A3] hover:border-[#404040]"
                }`}
                data-testid="transfer-mode-empresa"
                title={isEmpresaSource ? "Contrato já é Capital Próprio" : "Move este contrato para Capital Próprio (100% Forte Capital)"}
              >
                <Building2 size={14}/> Capital Próprio
              </button>
            </div>
          </div>

          {mode === "socio" && (
            <div>
              <label className="fc-label">Sócio destino</label>
              <select
                className="fc-input"
                value={newSocioId}
                onChange={e => setNewSocioId(e.target.value)}
                required
                data-testid="transfer-new-socio-select"
              >
                <option value="">— Selecione —</option>
                {socios.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.capital_disponivel !== undefined
                      ? `${s.name} (disponível: R$ ${(s.capital_disponivel ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })})`
                      : s.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {mode === "empresa" && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-[12px] text-emerald-200/90" data-testid="transfer-empresa-hint">
              <div className="flex items-center gap-2 mb-1 font-medium">
                <Building2 size={13}/> Capital Próprio (100% Forte Capital)
              </div>
              O vínculo com o sócio será removido. Todo o lucro deste contrato passa a ser 100% da empresa.
              Participação do sócio = 0%. Pode ser revertido a qualquer momento transferindo para outro sócio.
            </div>
          )}

          <div>
            <label className="fc-label">Motivo da transferência</label>
            <textarea
              rows={3}
              className="fc-input resize-none"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Ex: Cliente fechou com capital próprio da empresa; sócio pediu resgate; realocação de risco."
              required
              data-testid="transfer-reason"
            />
            <div className="text-[11px] text-[#737373] mt-1">
              Será gravado em auditoria com origem, destino, usuário, data/hora e motivo.
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#1f1f1f] bg-[#0a0a0a]/50">
          <button type="button" onClick={onClose} className="fc-btn-ghost text-sm">Cancelar</button>
          <button type="submit" disabled={busy} className="fc-btn-gold flex items-center gap-2 text-sm" data-testid="transfer-socio-submit">
            {busy && <Loader2 size={14} className="animate-spin"/>} Confirmar transferência
          </button>
        </div>
      </form>
    </div>
  );
}
